import { auth, db } from '../config/firebase';

export interface Template {
  id: string;
  name: string;
  html: string;
  category: string;
  createdAt: string;
  updatedAt: string;
  userId: string;
}

const templatesCollection = db.collection('templates');

export const saveTemplate = async (template: Omit<Template, 'id' | 'userId'>): Promise<Template> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be logged in to save templates');
  }

  const templateData = {
    ...template,
    userId: user.uid,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  const docRef = await templatesCollection.add(templateData);
  return {
    ...templateData,
    id: docRef.id,
  } as Template;
};

export const getUserTemplates = async (): Promise<Template[]> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be logged in to get templates');
  }

  const snapshot = await templatesCollection
    .where('userId', '==', user.uid)
    .orderBy('updatedAt', 'desc')
    .get();

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as Template[];
};

export const deleteTemplate = async (templateId: string): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be logged in to delete templates');
  }

  const templateRef = templatesCollection.doc(templateId);
  const template = await templateRef.get();

  if (!template.exists) {
    throw new Error('Template not found');
  }

  if (template.data()?.userId !== user.uid) {
    throw new Error('You do not have permission to delete this template');
  }

  await templateRef.delete();
};

export const updateTemplate = async (templateId: string, updates: Partial<Template>): Promise<Template> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be logged in to update templates');
  }

  const templateRef = templatesCollection.doc(templateId);
  const template = await templateRef.get();

  if (!template.exists) {
    throw new Error('Template not found');
  }

  if (template.data()?.userId !== user.uid) {
    throw new Error('You do not have permission to update this template');
  }

  const updateData = {
    ...updates,
    updatedAt: new Date().toISOString(),
  };

  await templateRef.update(updateData);

  return {
    id: templateId,
    ...template.data(),
    ...updateData,
  } as Template;
}; 